<template>
  <!-- The replica is a picture of the app with sample data: screen readers skip it and keyboard focus passes over it
       (the controls around it stay reachable). Mouse visitors can still click through the demo, which `inert` would block. -->
  <!-- The replica is the English app (mockups stay English), so it keeps left-to-right layout and punctuation
       on the Arabic and Hebrew editions too. -->
  <div ref="hostEl" class="product-scene-host" dir="ltr" aria-hidden="true"></div>
</template>

<script setup>
const props = defineProps({
  scene: { type: String, default: "dashboard" },
  compact: { type: Boolean, default: false },
  initialOpen: { type: Boolean, default: false },
});

const { injectCss } = useProductLab();
const hostEl = ref(null);
let api = null;
let themeObserver = null;

const syncTheme = () => {
  const root = hostEl.value?.querySelector(".faithful");
  if (root) {
    root.classList.toggle(
      "is-dark",
      document.documentElement.classList.contains("dark"),
    );
  }
};

// Offscreen replicas do not create DOM, load their fonts, or start demo loops.
// Keep a queued scene so a parent can select it before the lazy mount finishes.
let visibilityObserver = null;
let focusObserver = null;
const FOCUSABLE = 'a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
// The replica re-renders its markup on every demo click, so new controls are taken out of the tab order as they appear.
const keepOutOfTabOrder = () => {
  hostEl.value?.querySelectorAll(FOCUSABLE).forEach((el) => {
    if (el.getAttribute('tabindex') !== '-1') el.setAttribute('tabindex', '-1');
  });
};
let disposed = false;
let mounting = false;
let desiredScene = props.scene;
const emit = defineEmits(['ready']);

const mount = async () => {
  if (disposed || mounting) return;
  mounting = true;
  injectCss();
  try {
    // A runtime variable prevents Nuxt's public-asset plugin rewriting this import.
    const labModuleUrl = '/product-lab/replicas.js?v=motion-11';
    const mod = await import(/* @vite-ignore */ labModuleUrl);
    if (disposed || !hostEl.value) return;
    api?.destroy();
    api = mod.mountReplica(hostEl.value, {
      scene: desiredScene,
      compact: props.compact,
      initialOpen: props.initialOpen,
      onNotify: () => {},
    });
    syncTheme();
    keepOutOfTabOrder();
    focusObserver ??= new MutationObserver(keepOutOfTabOrder);
    focusObserver.observe(hostEl.value, { childList: true, subtree: true });
    emit('ready');
  } finally {
    mounting = false;
  }
};

onMounted(() => {
  themeObserver = new MutationObserver(syncTheme);
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  visibilityObserver = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    visibilityObserver.disconnect();
    mount();
  }, { rootMargin: '160px' });
  visibilityObserver.observe(hostEl.value);
});

onBeforeUnmount(() => {
  disposed = true;
  visibilityObserver?.disconnect();
  themeObserver?.disconnect();
  focusObserver?.disconnect();
  api?.destroy();
});

watch(() => props.compact, () => { if (api) mount(); });
watch(() => props.scene, (scene) => {
  desiredScene = scene;
  api?.setScene(scene);
});

defineExpose({
  setScene: (scene) => { desiredScene = scene; api?.setScene(scene); },
});
</script>

<style>
.product-scene-host {
  height: 100%;
  min-height: 320px;
  text-align: left;
}
.product-scene-host .replica {
  height: 100%;
}
</style>
